import type { RegionSelection } from './state'
import { cssSelector, domPath } from './selector'
import { icons } from './ui/icons'

// Marker tool: freehand ink on our own canvas layer (never the host DOM).
// Strokes are stored in PAGE coordinates so they scroll with content and can be
// re-rendered server-side. On confirm we compute the region box + the elements
// it intersects (region attribution) and the deepest common ancestor (anchor).

const PAD = 24 // region padding around the ink bbox

export interface MarkerCallbacks {
  isArmed(): boolean
  isOurs(e: Event): boolean
  onDrawStart(): void
  onConfirm(region: RegionSelection): void
  onClear(): void
}

export class Marker {
  private cbs: MarkerCallbacks
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private chip: HTMLElement
  private strokesPage: Array<Array<{ x: number; y: number }>> = []
  private current: Array<{ x: number; y: number }> = []
  private drawing = false
  private active = false
  private listeners: Array<[EventTarget, string, EventListener]> = []
  private cursorStyle: HTMLStyleElement | null = null

  constructor(root: ShadowRoot, cbs: MarkerCallbacks) {
    this.cbs = cbs
    this.canvas = document.createElement('canvas')
    this.canvas.className = 'qc-ink'
    this.ctx = this.canvas.getContext('2d')!
    this.chip = document.createElement('div')
    this.chip.className = 'qc-ink-chip'
    this.chip.hidden = true
    this.chip.innerHTML = `
      <button class="qc-ink-confirm" data-ref="confirm">${icons.check}<span>Report this area</span></button>
      <button class="qc-iconbtn" data-ref="clear" aria-label="Clear drawing">${icons.x}</button>
    `
    this.chip.querySelector('[data-ref="confirm"]')!.addEventListener('click', () => this.confirm())
    this.chip.querySelector('[data-ref="clear"]')!.addEventListener('click', () => this.clear())
    root.appendChild(this.canvas)
    root.appendChild(this.chip)
    this.resize()
    this.on(window, 'resize', () => this.resize())
    this.on(window, 'scroll', () => this.redraw(), { capture: true, passive: true })
  }

  /** Enable/disable the ink layer (armed marker tool). */
  setActive(active: boolean): void {
    this.active = active
    this.canvas.style.display = active ? 'block' : 'none'
    this.setCursor(active)
    if (active) this.installDrawListeners()
    else this.clear(true)
  }

  private setCursor(on: boolean): void {
    if (on && !this.cursorStyle) {
      this.cursorStyle = document.createElement('style')
      this.cursorStyle.setAttribute('data-qc-lens', 'marker-cursor')
      this.cursorStyle.textContent = 'html, html * { cursor: crosshair !important; }'
      document.head.appendChild(this.cursorStyle)
    } else if (!on && this.cursorStyle) {
      this.cursorStyle.remove()
      this.cursorStyle = null
    }
  }

  private installDrawListeners(): void {
    const down = (e: Event) => {
      if (!this.active || !this.cbs.isArmed()) return
      const pe = e as PointerEvent
      e.preventDefault()
      e.stopPropagation()
      this.drawing = true
      this.current = [this.toPage(pe)]
      if (!this.strokesPage.length) this.cbs.onDrawStart()
      this.canvas.setPointerCapture?.(pe.pointerId)
    }
    const move = (e: Event) => {
      if (!this.drawing) return
      const pe = e as PointerEvent
      this.current.push(this.toPage(pe))
      this.redraw()
    }
    const up = () => {
      if (!this.drawing) return
      this.drawing = false
      if (this.current.length > 1) this.strokesPage.push(this.current)
      this.current = []
      this.showChip()
    }
    // Pointer events on our own canvas (which has pointer-events:auto when active).
    this.on(this.canvas, 'pointerdown', down)
    this.on(window, 'pointermove', move)
    this.on(window, 'pointerup', up)
  }

  private toPage(e: PointerEvent): { x: number; y: number } {
    return { x: e.clientX + window.scrollX, y: e.clientY + window.scrollY }
  }

  private resize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.canvas.width = window.innerWidth * dpr
    this.canvas.height = window.innerHeight * dpr
    this.canvas.style.width = window.innerWidth + 'px'
    this.canvas.style.height = window.innerHeight + 'px'
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    this.redraw()
  }

  private redraw(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr)
    const all = this.current.length ? [...this.strokesPage, this.current] : this.strokesPage
    for (const stroke of all) {
      if (stroke.length < 1) continue
      for (const [color, width] of [
        ['rgba(255,255,255,0.9)', 5],
        ['#38BDF8', 3],
      ] as const) {
        this.ctx.strokeStyle = color
        this.ctx.lineWidth = width
        this.ctx.lineCap = 'round'
        this.ctx.lineJoin = 'round'
        this.ctx.beginPath()
        const p0 = this.toView(stroke[0])
        this.ctx.moveTo(p0.x, p0.y)
        for (const pt of stroke.slice(1)) {
          const v = this.toView(pt)
          this.ctx.lineTo(v.x, v.y)
        }
        this.ctx.stroke()
      }
    }
    if (!this.chip.hidden) this.positionChip()
  }

  private toView(p: { x: number; y: number }): { x: number; y: number } {
    return { x: p.x - window.scrollX, y: p.y - window.scrollY }
  }

  private showChip(): void {
    this.chip.hidden = false
    this.positionChip()
  }

  private positionChip(): void {
    const bbox = this.pageBBox()
    if (!bbox) {
      this.chip.hidden = true
      return
    }
    const vx = bbox.x - window.scrollX + bbox.w
    const vy = bbox.y - window.scrollY
    this.chip.style.left = Math.min(vx + 8, window.innerWidth - 190) + 'px'
    this.chip.style.top = Math.max(8, vy) + 'px'
  }

  private pageBBox(): { x: number; y: number; w: number; h: number } | null {
    const pts = this.strokesPage.flat()
    if (pts.length < 2) return null
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const p of pts) {
      minX = Math.min(minX, p.x)
      minY = Math.min(minY, p.y)
      maxX = Math.max(maxX, p.x)
      maxY = Math.max(maxY, p.y)
    }
    const x = Math.max(0, minX - PAD)
    const y = Math.max(0, minY - PAD)
    return { x, y, w: maxX - minX + PAD * 2, h: maxY - minY + PAD * 2 }
  }

  private confirm(): void {
    const box = this.pageBBox()
    if (!box) return
    const { elements, ancestor } = this.regionElements(box)
    const region: RegionSelection = { box, strokes: this.strokesPage, elements, ancestor }
    this.chip.hidden = true
    this.cbs.onConfirm(region)
  }

  /** Elements intersecting the region (deepest-first, capped) + deepest common ancestor. */
  private regionElements(box: { x: number; y: number; w: number; h: number }): {
    elements: Element[]
    ancestor: Element
  } {
    const vx = box.x - window.scrollX
    const vy = box.y - window.scrollY
    const hits = new Set<Element>()
    // Sample a grid of points across the region and collect the top page element at each.
    const step = 40
    for (let px = vx; px <= vx + box.w; px += step) {
      for (let py = vy; py <= vy + box.h; py += step) {
        const el = this.pageElementAt(px, py)
        if (el) hits.add(el)
      }
    }
    let elements = [...hits].filter((e) => this.intersects(e, vx, vy, box.w, box.h))
    // Deepest first.
    elements.sort((a, b) => depth(b) - depth(a))
    elements = elements.slice(0, 10)
    const ancestor = commonAncestor(elements) ?? document.body
    return { elements, ancestor }
  }

  private pageElementAt(x: number, y: number): Element | null {
    // Temporarily let events pass through our canvas to read the host element.
    const prev = this.canvas.style.pointerEvents
    this.canvas.style.pointerEvents = 'none'
    const el = document.elementFromPoint(x, y)
    this.canvas.style.pointerEvents = prev
    if (!el) return null
    const tag = el.tagName.toLowerCase()
    if (tag === 'html' || tag === 'body' || tag === 'qc-lens') return null
    return el
  }

  private intersects(el: Element, x: number, y: number, w: number, h: number): boolean {
    const r = el.getBoundingClientRect()
    return !(r.right < x || r.left > x + w || r.bottom < y || r.top > y + h)
  }

  clear(silent = false): void {
    this.strokesPage = []
    this.current = []
    this.drawing = false
    this.chip.hidden = true
    this.redraw()
    if (!silent) this.cbs.onClear()
  }

  get hasInk(): boolean {
    return this.strokesPage.length > 0
  }

  destroy(): void {
    for (const [t, type, fn] of this.listeners) t.removeEventListener(type, fn as EventListener)
    this.listeners = []
    this.setCursor(false)
    this.canvas.remove()
    this.chip.remove()
  }

  private on(t: EventTarget, type: string, fn: EventListener, opts?: AddEventListenerOptions): void {
    t.addEventListener(type, fn, opts)
    this.listeners.push([t, type, fn])
  }
}

function depth(el: Element): number {
  let d = 0
  let n: Element | null = el
  while ((n = n.parentElement)) d++
  return d
}

function commonAncestor(els: Element[]): Element | null {
  if (!els.length) return null
  let anc: Element | null = els[0]
  for (const el of els.slice(1)) {
    anc = lca(anc!, el)
    if (!anc) return null
  }
  return anc
}

function lca(a: Element, b: Element): Element | null {
  const ancestors = new Set<Element>()
  let n: Element | null = a
  while (n) {
    ancestors.add(n)
    n = n.parentElement
  }
  n = b
  while (n) {
    if (ancestors.has(n)) return n
    n = n.parentElement
  }
  return null
}

/** Build the region target for a report (kind='region'). */
export function buildRegionTarget(region: RegionSelection, redact: string[]): {
  region_box: { x: number; y: number; w: number; h: number }
  region_elements: Array<{ css_selector: string; dom_path: string }>
  ancestorSelector: string
  ancestorPath: string
} {
  void redact
  return {
    region_box: { x: Math.round(region.box.x), y: Math.round(region.box.y), w: Math.round(region.box.w), h: Math.round(region.box.h) },
    region_elements: region.elements.map((e) => ({ css_selector: cssSelector(e), dom_path: domPath(e) })),
    ancestorSelector: cssSelector(region.ancestor),
    ancestorPath: domPath(region.ancestor),
  }
}
